// Session ARA (S-14): a map/list of one day's dispatched work, grouped by engineer and put into a
// sensible visit order, with a rough total drive time. Opened from SchedulingPage's own header row.
//
// A well-ordered LIST, not an embedded map: an actual map needs a Maps JS API key and a new client
// dependency this session does not have configured (see server/src/integrations/maps/client.ts's own
// comment -- a SEPARATE, billing-enabled key from the Drive/Gmail OAuth client already wired up).
// The server route already uses that same integration for the DRIVE TIME figure when a key is
// present, so a future session that adds the map component only has to render it -- the ordering and
// timing already come from the real thing when it is connected.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, api } from "../../lib/api";
import type { DayRoute, EngineerDayRoute } from "../../lib/api";
import JobTypeBadge from "../../components/JobTypeBadge";
import { Loader } from "../../components/Loader";
import { ModalShell } from "../../components/ModalShell";

function todayString(): string {
  return new Date().toISOString().slice(0, 10);
}

function driveTimeLine(route: EngineerDayRoute): string {
  if (route.driveMinutes != null) return `~${route.driveMinutes} min driving (Google Maps)`;
  const routable = route.stops.length - route.unroutedCount;
  return routable >= 2 ? "Order estimated by postcode only, no drive-time figure" : "Not enough stops to estimate a route";
}

function StopRow({ stop, index }: { stop: EngineerDayRoute["stops"][number]; index: number | null }) {
  return (
    <li className="flex items-start gap-2 rounded-ele border border-ele-purple-accent/60 px-2.5 py-1.5 text-sm">
      {index != null && (
        <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-ele-purple-accent/30 font-menu text-[11px] text-ele-strong">
          {index}
        </span>
      )}
      <span className="min-w-0 flex-1">
        {/* Session ATD (S-8 follow-up): a real deep link to the site (and, on the reference line
            below, the lift), when this stop carries their ids -- the same styling other record
            pages use for a cross-link. A stop with no site or lift resolved (a lift-less repair, an
            unrouted call-out) keeps the plain text it always had. */}
        {stop.siteId ? (
          <Link to={`/office/sites/${encodeURIComponent(stop.siteId)}`} className="block truncate text-ele-green underline-offset-2 hover:underline">
            {stop.siteName || "Unknown site"}
          </Link>
        ) : (
          <span className="block truncate text-ele-strong">{stop.siteName || "Unknown site"}</span>
        )}
        <span className="block text-xs text-ele-body">
          {stop.assetId ? (
            <Link to={`/office/lifts/${encodeURIComponent(stop.assetId)}`} className="text-ele-green underline-offset-2 hover:underline">
              {stop.reference ?? stop.eleNumber}
            </Link>
          ) : (
            stop.reference ?? stop.eleNumber
          )}
          {(stop.reference ?? stop.eleNumber) && " | "}
          {stop.sitePostcode ?? "No postcode on file"}
        </span>
      </span>
      <JobTypeBadge type={stop.type} equipmentAsset={stop.equipmentAsset} size="sm" />
    </li>
  );
}

export function DayRouteModal({
  initialDate,
  engineers,
  onClose,
}: {
  initialDate: Date; // a UTC-midnight Date, e.g. SchedulingPage's own `cursor`
  engineers: { id: string; name: string }[];
  onClose: () => void;
}) {
  const [dateStr, setDateStr] = useState(() => {
    const iso = initialDate.toISOString().slice(0, 10);
    return iso || todayString();
  });
  const [engineerId, setEngineerId] = useState("");
  const [data, setData] = useState<DayRoute | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.scheduling
      .dayRoute(dateStr, engineerId || undefined)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError && err.message ? err.message : "Could not load the route. Please try again.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dateStr, engineerId]);

  function engineerName(id: string): string {
    return engineers.find((e) => e.id === id)?.name ?? "Engineer";
  }

  const isEmpty = data != null && data.routes.length === 0 && data.unassigned.length === 0;

  return (
    <ModalShell ariaLabel="Today's route" onClose={onClose} closeOnBackdrop width="max-w-[640px]" panelClassName="p-6 max-h-[85vh] overflow-y-auto">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl text-ele-strong">Day route</h2>
          <p className="text-sm text-ele-body">Each engineer's dispatched work for the day, in a sensible visit order.</p>
        </div>
        <button onClick={onClose} aria-label="Close" className="text-ele-body hover:text-ele-green">
          ✕
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1.5 text-sm text-ele-body">
          Date
          <input
            type="date"
            value={dateStr}
            onChange={(e) => setDateStr(e.target.value)}
            className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-sm text-ele-strong outline-none focus:border-ele-green"
          />
        </label>
        <label className="flex items-center gap-1.5 text-sm text-ele-body">
          Engineer
          <select
            value={engineerId}
            onChange={(e) => setEngineerId(e.target.value)}
            className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-sm text-ele-strong outline-none focus:border-ele-green"
          >
            <option value="">All engineers</option>
            {engineers.map((eng) => (
              <option key={eng.id} value={eng.id}>
                {eng.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">{error}</p>}

      {loading ? (
        <Loader rows={3} />
      ) : isEmpty ? (
        <p className="rounded-ele border border-ele-purple-accent/60 bg-ele-surface px-3 py-2 text-sm text-ele-body">
          No dispatched work on this day.
        </p>
      ) : (
        <div className="space-y-4">
          {data?.routes.map((route) => (
            <div key={route.engineerId} className="rounded-ele border border-ele-purple-accent p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <h3 className="font-heading text-base text-ele-strong">{engineerName(route.engineerId)}</h3>
                <span className="text-xs text-ele-body">{driveTimeLine(route)}</span>
              </div>
              <ol className="space-y-1.5">
                {route.stops.map((stop, i) => (
                  <StopRow key={stop.id} stop={stop} index={i < route.stops.length - route.unroutedCount ? i + 1 : null} />
                ))}
              </ol>
            </div>
          ))}

          {data && data.unassigned.length > 0 && (
            <div className="rounded-ele border border-ele-purple-accent/60 p-3">
              <h3 className="mb-2 font-heading text-base text-ele-strong">Not yet assigned</h3>
              <ol className="space-y-1.5">
                {data.unassigned.map((stop) => (
                  <StopRow key={stop.id} stop={stop} index={null} />
                ))}
              </ol>
            </div>
          )}
        </div>
      )}

      <p className="mt-4 text-xs text-ele-body opacity-70">
        {data?.usedLiveDriveTimes
          ? "Drive times are from Google Maps."
          : "Visit order is estimated by postcode proximity: Google Maps is not connected, so no drive-time figure is shown."}
      </p>
    </ModalShell>
  );
}
